import { T } from '../../copy/text'
import type { PendingDecision } from './queries'
import {
  RED_KINDS,
  RED_QUESTIONS,
  frozenScopesAreReal,
  hasAlternative,
  isExpired,
  onExpired,
  primaryOption,
  type RedDecision,
} from './redline'
import { useState } from 'react'

import { mayUserPick, type ModelOption } from './model-picker'
import {
  SUB_FAIL_EXITS,
  evidenceForWorkItem,
  mayAutoRun,
  type Evidence,
  type SubFailExitKind,
} from './subfail'

/**
 * 「待决定」页签（W15）。
 *
 * 判断全部来自 `redline.ts` 的纯函数——这个组件只管画。
 * 那些函数每一条都对应一句验收（五类穷举、主按钮是推荐方案、
 * 写域真的冻了、过期只看时间），而它们能在没有 DOM 的地方被验。
 */
export function DecisionsPanel({
  decisions,
  now,
  onAnswer,
}: {
  readonly decisions: readonly PendingDecision[]
  readonly now: Date
  /** 真提交一个决定。不给就点不动——**不假装提交了**。 */
  readonly onAnswer?: (decisionId: string, approve: boolean) => Promise<void>
}) {
  if (decisions.length === 0) {
    return (
      <p data-testid="lulu-decisions-empty" className="text-npc-caption text-npc-lulu-t3">
        <T id="lulu_workbench.red.never_for_you" />
      </p>
    )
  }
  return (
    <ul data-testid="lulu-panel-decisions" className="grid gap-2">
      {decisions.map((d) => {
        const decision = toRedDecision(d)
        const expired = isExpired(decision, now)
        const primary = primaryOption(decision)
        return (
          <li
            key={d.id}
            data-testid="lulu-decision-card"
            data-kind={decision.kind}
            data-expired={expired ? 'yes' : 'no'}
            className="rounded-npc-card border border-npc-lulu-line-2 bg-npc-lulu-bg-2 p-3"
          >
            <p className="text-npc-caption text-npc-lulu-t1">{d.operation}</p>

            {/* 过期了就重新问，**绝不默认执行**。onExpired 的返回类型
                只有一个值，所以这里没有「过期即执行」那一支可写。 */}
            {expired ? (
              <p
                data-testid="lulu-decision-expired"
                className="mt-1 text-npc-meta text-npc-lulu-warn"
              >
                <T id="lulu_workbench.red.expired" />
                <span hidden>{onExpired()}</span>
              </p>
            ) : null}

            {/* 写域冻结那句是一句关于此刻的断言，**冻了才说**。 */}
            {frozenScopesAreReal(decision) ? (
              <p data-testid="lulu-decision-frozen" className="mt-1 text-npc-meta text-npc-lulu-t3">
                <T id="lulu_workbench.red.scopes_frozen" />
              </p>
            ) : null}

            {/* 主按钮是推荐方案，不是「直接删」。找不到推荐方案就不给主按钮。 */}
            {primary ? (
              <button
                type="button"
                data-testid="lulu-decision-primary"
                data-option={primary.id}
                // **过期的卡不许提交。** 过期之后的唯一一条路是重新问，
                // 而一个还能按下去的过期按钮，按下去就是拿一个用户
                // 早已看不到上下文的决定去执行。
                disabled={expired || onAnswer === undefined}
                onClick={() => void onAnswer?.(d.id, true)}
                className="mt-2 rounded-npc-button bg-npc-lulu-ok px-3 py-1 text-npc-caption text-npc-lulu-bg-0 disabled:opacity-40"
              >
                {primary.label}
                <span className="ml-1 text-npc-meta">
                  <T id="lulu_workbench.red.recommended" />
                </span>
              </button>
            ) : null}

            {/* ## 拒绝
                //
                // 这一半在 2026-08-28 之前**根本不存在**，而它是承重的：
                //
                //   - 同一天 B0 把「批准红级」收给了 team.CapApproveRed（只有老板），
                //     **拒绝仍然是 CapOperate**——干活的现在唯一能做的就是拒绝，
                //     而界面上没有那个按钮
                //   - 没有它，一条待批只有老板点「确认」才关得掉。
                //     一条谁都关不掉的待批会永远挂在决定面板上，
                //     而那比放开批准更糟
                //
                // 文案用 chat.lulu.receipts.reject（「拒绝」）——它与
                // chat.lulu.receipts.approve 成对，两条都标 source: Lulu V2，
                // 旧 /chat 屏那张回执卡上一直是完整的一对。**不是新编的。**
                //
                // ### 过期了也能拒绝，与主按钮刻意相反
                //
                // 主按钮过期就禁用，理由是「拿一个用户早已看不到上下文的决定
                // 去执行」。而拒绝**不产生任何效果**，它只是把这条关掉——
                // 一张过期且拒绝不掉的卡，正好是上面那个「谁都关不掉」。
            */}
            <button
              type="button"
              data-testid="lulu-decision-reject"
              disabled={onAnswer === undefined}
              onClick={() => void onAnswer?.(d.id, false)}
              className="ml-2 mt-2 rounded-npc-button border border-npc-lulu-line-2 px-3 py-1 text-npc-caption text-npc-lulu-t2 disabled:opacity-40"
            >
              <T id="chat.lulu.receipts.reject" />
            </button>

            {hasAlternative(decision) ? null : (
              <p
                data-testid="lulu-decision-no-alt"
                className="mt-1 text-npc-meta text-npc-lulu-bad"
              >
                <T id="lulu_workbench.red.q_alternative" />
              </p>
            )}

            <ul className="mt-2 grid gap-0.5">
              {RED_QUESTIONS.map((q) => (
                <li
                  key={q}
                  data-testid="lulu-decision-question"
                  className="text-npc-meta text-npc-lulu-t4"
                >
                  <T id={q} />
                </li>
              ))}
            </ul>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * 后端的 `PendingDecision` → `redline` 的 `RedDecision`。
 *
 * **认不出的 operation 一律落到 `delete_data`** 是不行的，所以这里
 * 认不出时用第一个红级类别并把 `frozenScopes` 留空——空写域会让
 * `frozenScopesAreReal` 回 false，于是界面**不会**说「已冻结」。
 * 一句我们不知道真假的保证，不说比说了强。
 */
function toRedDecision(d: PendingDecision): RedDecision {
  const out = d.output as Record<string, unknown>
  const kind = RED_KINDS.find((k) => k === out['red_kind']) ?? RED_KINDS[0]
  const scopes = Array.isArray(out['frozen_scopes'])
    ? (out['frozen_scopes'] as unknown[]).filter((x): x is string => typeof x === 'string')
    : []
  const options = Array.isArray(out['options'])
    ? (out['options'] as Record<string, unknown>[]).map((o, i) => ({
        id: typeof o['id'] === 'string' ? o['id'] : `opt-${i}`,
        label: typeof o['label'] === 'string' ? o['label'] : '',
        recommended: o['recommended'] === true,
        destructive: o['destructive'] === true,
      }))
    : []
  return {
    id: d.id,
    kind,
    frozenScopes: scopes,
    options,
    expiresAt: typeof out['expires_at'] === 'string' ? out['expires_at'] : d.created_at,
  }
}

/**
 * 「证据」页签。子 Agent 的产出挂在父工作项下面，可反查。
 */
export function EvidencePanel({
  evidence,
  workItemId,
}: {
  readonly evidence: readonly Evidence[]
  readonly workItemId: string | null
}) {
  const rows = workItemId === null ? evidence : evidenceForWorkItem(evidence, workItemId)
  if (rows.length === 0) {
    return (
      <p data-testid="lulu-evidence-empty" className="text-npc-caption text-npc-lulu-t3">
        <T id="lulu_workbench.evidence.empty" />
      </p>
    )
  }
  return (
    <ul data-testid="lulu-panel-evidence" className="grid gap-1.5">
      {rows.map((e) => (
        <li key={e.id} data-testid="lulu-evidence-row" data-work-item={e.workItemId}>
          <p className="text-npc-caption text-npc-lulu-t2">{e.label}</p>
          <p className="text-npc-meta text-npc-lulu-t4">
            <T id="lulu_workbench.evidence.on_parent" vars={{ item: e.workItemId }} />
          </p>
        </li>
      ))}
    </ul>
  )
}

/**
 * 子 Agent 部分失败时的三个出口（S04）。
 *
 * 花钱那条**不给自动执行的入口**——`mayAutoRun` 回 false 的那条
 * 只渲染成需要点击的按钮，并把「这一条要花钱」写在旁边。
 */
export function SubFailExits({
  subrunId,
  currentAdapter,
  alternatives,
  onRecover,
}: {
  /** 要补救的那个失败子 Agent。没有就三颗都点不动。 */
  readonly subrunId?: string
  /** 失败那个当前用的运行时。**换模型的候选里要把它排掉。** */
  readonly currentAdapter?: string | null
  /** 可换的运行时。空或只剩当前那个时，「换个模型」点不动。 */
  readonly alternatives?: readonly ModelOption[] | null
  /**
   * 真发补救请求。`swap_model` 必须带一个**明确的**替代项——
   * 由用户在下面那个列表里点出来，不在这个组件里挑一个：
   * 挑出来的那个可能正好是原来那个，而那样 swap 就变成了 retry。
   */
  readonly onRecover?: (kind: SubFailExitKind, adapter?: string) => Promise<void>
}) {
  const [pending, setPending] = useState<SubFailExitKind | null>(null)
  const [failed, setFailed] = useState<SubFailExitKind | null>(null)
  const [picking, setPicking] = useState(false)

  // 能换的运行时：**把当前这个排掉**。换成一样的等于没换，
  // 而后端会以 409 拒掉——与其让用户点一次再看到报错，
  // 不如根本不把它列出来。
  const swappable = (alternatives ?? []).filter((m) => m.id !== currentAdapter && mayUserPick(m))

  const run = async (kind: SubFailExitKind, adapter?: string) => {
    if (!onRecover || !subrunId) return
    if (kind === 'swap_model' && !adapter) {
      // 换模型没挑出替代项就先让他挑。**不替他挑一个。**
      setPicking(true)
      return
    }
    setFailed(null)
    setPending(kind)
    setPicking(false)
    try {
      await onRecover(kind, adapter)
    } catch {
      // 失败不假成功：把那一条标出来，三颗都留着可以再点。
      setFailed(kind)
    } finally {
      setPending(null)
    }
  }
  return (
    <ul data-testid="lulu-subfail-exits" className="grid gap-1">
      {SUB_FAIL_EXITS.map((exit) => (
        <li key={exit.kind}>
          <button
            type="button"
            data-testid="lulu-subfail-exit"
            data-kind={exit.kind}
            data-auto={mayAutoRun(exit) ? 'yes' : 'no'}
            data-preferred={exit.preferred ? 'yes' : 'no'}
            data-pending={pending === exit.kind ? 'yes' : 'no'}
            disabled={
              pending !== null ||
              !onRecover ||
              !subrunId ||
              // 换模型这条：没有可换的就点不动。**不是点了再报错**——
              // 一颗点下去必然失败的按钮，比一颗禁用的按钮更让人困惑。
              (exit.kind === 'swap_model' && swappable.length === 0)
            }
            onClick={() => void run(exit.kind)}
            className="w-full rounded-npc-chip border border-npc-lulu-line-2 px-2 py-1 text-left text-npc-caption text-npc-lulu-t2 disabled:opacity-40"
          >
            <T id={exit.copyId} />
          </button>
          {exit.kind === 'swap_model' && picking ? (
            <ul data-testid="lulu-subfail-adapters" className="mt-1 grid gap-0.5 pl-2">
              {swappable.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    data-testid="lulu-subfail-adapter"
                    data-adapter={m.id}
                    onClick={() => void run('swap_model', m.id)}
                    className="w-full rounded-npc-chip-inline px-1 py-0.5 text-left text-npc-meta text-npc-lulu-t2"
                  >
                    {m.name}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {failed === exit.kind ? (
            <p data-testid="lulu-subfail-error" className="px-2 text-npc-meta text-npc-lulu-bad">
              <T id="lulu_workbench.subfail.failed" />
            </p>
          ) : null}
          {exit.costsMoney ? (
            <p data-testid="lulu-subfail-costs" className="px-2 text-npc-meta text-npc-lulu-warn">
              <T id="lulu_workbench.subfail.costs_money" />
            </p>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
